import { useMemo } from 'react';
import { FilterOption } from 'models/Filters';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import utils, { getSortBy, getIsDesc, getHasElementsInArray } from 'utils';
import {
  EMPTY_ARRAY,
  MULTI_SORT_SEPARATOR,
  SORT_PARAM_KEYWORD,
} from 'data/global';
import useQueryParams from 'hooks/useQueryParams';
import {
  createText,
  getRangesFromParams,
  mergeRanges,
} from 'components/RangeFilterWrapper/utils';

export type FilterPill = {
  key: string;
  label: string;
  displayValue: string | null;
  onRemove: () => void;
  onLabelClick?: (value: string) => void;
};

export type SortPill = {
  key: string;
  field: string;
  label: string;
  isDesc: boolean;
  onRemove: () => void;
};

const parseParamValues = (rawValue?: string): string[] => {
  if (!rawValue) return [];
  const asArray = utils.mapParamsToArray(rawValue);
  return getHasElementsInArray(asArray) ? asArray : [rawValue];
};

const useActiveFilterSortPills = (
  filterOptions: FilterOption[],
  sortOptions: OptionValue<string>[],
  sortParam: string = SORT_PARAM_KEYWORD,
  sortClearFields: string[] = EMPTY_ARRAY,
  onChipLabelClick?: (fieldKey: string) => void,
) => {
  const { params, removeSeveralQueryParams, setQueryParam } =
    useQueryParams<Record<string, string>>();

  const sort = params[sortParam];

  const filterPills = useMemo<FilterPill[]>(
    () =>
      filterOptions.reduce<FilterPill[]>((acc, option) => {
        const rawIdValue = params[option.value];
        if (!rawIdValue) return acc;

        const isSearchable = !('options' in option);
        const removeKeys = [option.value, ...(option.clearFields ?? [])];

        let displayValue: string | null = null;

        if ('isRange' in option && option.isRange) {
          const ranges = getRangesFromParams(
            params[option.clearFields?.[0] ?? ''],
            rawIdValue,
          );

          displayValue = getHasElementsInArray(ranges)
            ? mergeRanges(ranges).map(createText).join(', ')
            : null;
        } else {
          const displayParamKey = isSearchable
            ? option.clearFields?.[0] ?? option.value
            : option.value;

          const rawDisplayValue = params[displayParamKey] ?? rawIdValue;

          const displayValues = option.isMultiple
            ? parseParamValues(rawDisplayValue)
            : [rawDisplayValue];

          if (displayValues.length === 1) {
            if ('options' in option) {
              const match = option.options.find(
                ({ value }) => value === displayValues[0],
              );
              displayValue = match?.label ?? displayValues[0];
            } else {
              displayValue = displayValues[0];
            }
          } else if (displayValues.length > 1) {
            displayValue = String(displayValues.length);
          }
        }

        acc.push({
          key: option.value,
          label: option.label,
          displayValue,
          onRemove: () => removeSeveralQueryParams(removeKeys),
          onLabelClick: onChipLabelClick,
        });

        return acc;
      }, []),
    [filterOptions, params, onChipLabelClick, removeSeveralQueryParams],
  );

  const sortPills = useMemo<SortPill[]>(() => {
    if (!sort) return [];

    return sort
      .split(MULTI_SORT_SEPARATOR)
      .filter(Boolean)
      .map((entry) => {
        const field = getSortBy(entry);
        const isDesc = getIsDesc(entry);
        const label = String(
          sortOptions.find(({ value }) => value === field)?.label ?? field,
        );

        return {
          key: `sort-${field}`,
          field,
          label,
          isDesc,
          onRemove: () => {
            const remaining = sort
              .split(MULTI_SORT_SEPARATOR)
              .filter((e) => getSortBy(e) !== field)
              .join(MULTI_SORT_SEPARATOR)
              .trim();

            if (remaining) {
              setQueryParam(sortParam, remaining);
            } else {
              removeSeveralQueryParams([sortParam, ...sortClearFields]);
            }
          },
        };
      });
  }, [
    sort,
    sortOptions,
    sortParam,
    sortClearFields,
    setQueryParam,
    removeSeveralQueryParams,
  ]);

  const hasPills =
    getHasElementsInArray(sortPills) || getHasElementsInArray(filterPills);

  return { filterPills, sortPills, hasPills };
};

export default useActiveFilterSortPills;
